import Avatar from '@/components/base/Avatar';
import { formatDateTime } from '@/utils/format';
import type { DeliverableDecision, DeliverableFeedback } from '@/types/portal';

const DECISION_META: Record<
  DeliverableDecision,
  { label: string; icon: string; wrap: string } | undefined
> = {
  approved: {
    label: 'Approved',
    icon: 'ri-check-line',
    wrap: 'bg-accent-100 text-accent-800 border-accent-200',
  },
  changes_requested: {
    label: 'Changes requested',
    icon: 'ri-edit-2-line',
    wrap: 'bg-primary-100 text-primary-800 border-primary-200',
  },
  rejected: {
    label: 'Rejected',
    icon: 'ri-close-line',
    wrap: 'bg-primary-100 text-primary-800 border-primary-200',
  },
  comment: undefined,
};

export default function DeliverableFeedbackThread({
  items,
}: {
  items: DeliverableFeedback[];
}) {
  if (items.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-4 text-center text-xs text-foreground-500">
        No feedback yet. Leave a note or a decision below.
      </p>
    );
  }

  return (
    <ol className="space-y-3">
      {items.map((item) => {
        const meta = item.decision ? DECISION_META[item.decision] : undefined;
        return (
          <li key={item.id} className="flex items-start gap-3">
            <Avatar src={item.avatar} alt={item.author} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-medium text-foreground-900">{item.author}</span>
                <span className="text-[11px] text-foreground-400">{item.authorRole}</span>
                <span className="text-[11px] text-foreground-400">· {formatDateTime(item.at)}</span>
              </div>
              {meta ? (
                <span
                  className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-label font-medium ${meta.wrap}`}
                >
                  <i className={meta.icon} />
                  {meta.label}
                </span>
              ) : null}
              <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
              {item.timecode ? (
                <span className="mt-1 inline-flex items-center gap-1 rounded-md border border-background-200 bg-background-100 px-1.5 py-0.5 text-[11px] font-label text-foreground-600">
                  <i className="ri-time-line" />
                  At {item.timecode}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}